<script setup lang="ts">
import { onMounted, ref } from 'vue';

import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import demographicQuestions from '@/components/questions/demographicQuestions.vue';
import nameQuestions from '@/components/questions/nameQuestions.vue';

import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { getLabel } from '@/utils/data-display-utils';
import { useUserStore } from '@/store/userStore';
import { userService } from '@/services/user.service';
import { Locales } from '@/store/language.interface';

const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// REFS
const demographicDialogRef = ref<InstanceType<typeof BaseDialog>>();
const hasUserName = ref<boolean>();
const regex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
const userStore = useUserStore();

// EMITS
const emit = defineEmits<{
  (e: 'submit'): void;
}>();

// LIFECYCLE
onMounted(() => {
  userStore.loadGenders(props.languageEnabled);
  hasUserName.value = userStore.validateUserName;
});

// VALIDATIONS
const validationSchema = yup.object().shape({
  postalCode: yup
    .string()
    .matches(regex, () => getLabel('errors.type.postal-code', props.languageEnabled))
    .required(() => getLabel('errors.required', props.languageEnabled)),
  gender: yup.object().required(() => getLabel('errors.required', props.languageEnabled)),
  birthYear: yup
    .number()
    .required(() => getLabel('errors.required', props.languageEnabled))
    .integer(() => getLabel('errors.birth-date.integer', props.languageEnabled))
    .typeError(() => getLabel('errors.birth-date.number', props.languageEnabled))
    .min(1900, () => getLabel('errors.birth-date.min', props.languageEnabled))
    .max(new Date().getFullYear(), () => getLabel('errors.birth-date.max', props.languageEnabled)),
  firstName: yup.string().required(() => getLabel('errors.required', props.languageEnabled)),
  lastName: yup.string().required(() => getLabel('errors.required', props.languageEnabled)),
});

const { handleSubmit, meta, resetForm } = useForm({
  validationSchema,
});

// FUNCTIONS
function openDialog() {
  demographicDialogRef.value?.openDialog();
}

function closeDialog() {
  demographicDialogRef.value?.closeDialog();
  resetForm();
}

const onSubmit = handleSubmit(async (values) => {
  const userData = {
    year_birth: values.birthYear,
    postal_code: values.postalCode,
    gender: values.gender.value,
    email: userStore.user.email,
    last_name: values.lastName,
    first_name: values.firstName,
  };
  const response = await userService.updateUser(userData);
  if (response.success) {
    emit('submit');
    closeDialog();
  }
});

defineExpose({
  openDialog,
  closeDialog,
});
</script>

<template>
  <BaseDialog ref="demographicDialogRef">
    <template v-slot:title>
      <div class="demo-title">
        {{ getLabel('demographics.title', props.languageEnabled) }}
      </div>
    </template>
    <template v-slot:content>
      <form class="demo-data">
        <div class="demo-data__info">
          <div class="demo-data__name-label">
            {{ getLabel('demographics.info', props.languageEnabled) }}
          </div>
          <demographic-questions :language-enabled="props.languageEnabled" />
        </div>
        <div class="demo-data__name">
          <div class="demo-data__name-label">
            {{ getLabel('demographics.comment', props.languageEnabled) }}
          </div>
          <name-questions :language-enabled="props.languageEnabled" />
        </div>
        <div class="demo-actions">
          <BaseButton :disabled="!meta.valid" class="action" @click="onSubmit">{{
            getLabel('buttons.submit', props.languageEnabled)
          }}</BaseButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<style lang="scss">
.demo {
  &-data {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &__name {
      padding: $space-sm 0 0;
    }

    &__name-label,
    &__info-label {
      padding: 0 0 $space-sm;
      font-size: $small-text;
    }
  }

  &-title {
    font-size: $small-title;
  }

  &-actions {
    display: flex;
    justify-content: end;
    margin-top: $space-md;
  }
}

.base-dialog {
  background-color: $color-neutral-background !important;

  &__title {
    padding: $space-md $space-xl 0 $space-md !important;
  }

  &__content {
    padding: $space-sm $space-md $space-md !important;
  }
}

.error {
  color: $color-red;
}
</style>
